import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Save, Mail, Phone, MapPin, Globe, Instagram, Facebook, Twitter, Clock } from 'lucide-react';
import './AdminSettings.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    contact_email: '',
    contact_phone: '',
    whatsapp_link: '',
    address: '',
    locations: [],
    social_media: {
      instagram: '',
      facebook: '',
      twitter: ''
    },
    business_hours: '',
    google_maps_embed: '',
    announcement_banner: '',
    announcement_active: false
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(
        `${API}/admin/settings`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Settings updated successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSocialMediaChange = (platform, value) => {
    setFormData({
      ...formData,
      social_media: {
        ...formData.social_media,
        [platform]: value
      }
    });
  };

  if (loading) {
    return <div className="loading">Loading settings...</div>;
  }

  return (
    <div className="admin-settings">
      <div className="settings-header">
        <div>
          <h2>Website Settings</h2>
          <p>Configure your website information and preferences</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="settings-form">
        {/* Contact Information */}
        <div className="settings-section">
          <h3>Contact Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>
                <Mail size={16} /> Email Address *
              </label>
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>
                <Phone size={16} /> Phone Number *
              </label>
              <input
                type="tel"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>
                <Phone size={16} /> WhatsApp Link
              </label>
              <input
                type="url"
                value={formData.whatsapp_link || ''}
                onChange={(e) => setFormData({ ...formData, whatsapp_link: e.target.value })}
                placeholder="https://wa.me/..."
              />
            </div>

            <div className="form-group">
              <label>
                <MapPin size={16} /> Address *
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
              />
            </div>
          </div>
        </div>

        {/* Business Hours */}
        <div className="settings-section">
          <h3>Business Hours</h3>
          <div className="form-group">
            <label>
              <Clock size={16} /> Operating Hours *
            </label>
            <input
              type="text"
              value={formData.business_hours}
              onChange={(e) => setFormData({ ...formData, business_hours: e.target.value })}
              placeholder="Monday - Sunday: 9:00 AM - 10:00 PM"
              required
            />
          </div>
        </div>

        {/* Social Media */}
        <div className="settings-section">
          <h3>Social Media Links</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>
                <Instagram size={16} /> Instagram
              </label>
              <input
                type="text"
                value={formData.social_media?.instagram || ''}
                onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
                placeholder="@tropiluxe"
              />
            </div>

            <div className="form-group">
              <label>
                <Facebook size={16} /> Facebook
              </label>
              <input
                type="text"
                value={formData.social_media?.facebook || ''}
                onChange={(e) => handleSocialMediaChange('facebook', e.target.value)}
                placeholder="TropiluxeJamaica"
              />
            </div>

            <div className="form-group">
              <label>
                <Twitter size={16} /> Twitter
              </label>
              <input
                type="text"
                value={formData.social_media?.twitter || ''}
                onChange={(e) => handleSocialMediaChange('twitter', e.target.value)}
                placeholder="@tropiluxe"
              />
            </div>
          </div>
        </div>

        {/* Google Maps */}
        <div className="settings-section">
          <h3>Google Maps</h3>
          <div className="form-group">
            <label>
              <Globe size={16} /> Google Maps Embed Code
            </label>
            <textarea
              value={formData.google_maps_embed || ''}
              onChange={(e) => setFormData({ ...formData, google_maps_embed: e.target.value })}
              rows="3"
              placeholder="<iframe src='...' ...</iframe>"
            />
            <small>Paste the embed code from Google Maps</small>
          </div>
        </div>

        {/* Announcement Banner */}
        <div className="settings-section">
          <h3>Announcement Banner</h3>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.announcement_active}
                onChange={(e) => setFormData({ ...formData, announcement_active: e.target.checked })}
              />
              <span>Show announcement banner on website</span>
            </label>
          </div>
          <div className="form-group">
            <label>Announcement Text</label>
            <textarea
              value={formData.announcement_banner || ''}
              onChange={(e) => setFormData({ ...formData, announcement_banner: e.target.value })}
              rows="2"
              placeholder="Special offer! Book now and get 20% off..."
            />
          </div>
        </div>

        <div className="settings-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminSettings;
